import { useMemo, useState } from 'react'

import moment from 'moment-timezone'

import { useGetWebhooks } from 'data/hooks/dataConnections/useGetWebhooks'
import { useSyncConnection } from 'data/hooks/dataConnections/useSyncConnection'
import { useObject } from 'data/hooks/objects'
import { getIsStackerNativeObject } from 'features/admin/stackerNativeObjectUtils'
import { DC_TYPE_TO_INTEGRATION_ID } from 'features/DataConnections/constants'
import { calculateSyncState } from 'features/DataConnections/DataConnectionsManager/calculateSyncState'
import { calculateWebhookState } from 'features/DataConnections/DataConnectionsManager/calculateWebhooksState'

import { useToast } from 'ui/components/Toast'
import { theme } from 'ui/styling/Theme.css'

import { useToolbarContext } from './useToolbarContext'

export function useToolbarActionsSyncState() {
    const [isOpen, setIsOpen] = useState(false)

    const { dataConnection, objectSid } = useToolbarContext()

    const { object } = useObject(objectSid)
    const isStackerNativeObject = getIsStackerNativeObject(object)

    const dataConnections = useMemo(() => {
        return dataConnection ? [dataConnection] : []
    }, [dataConnection])

    const integrationId = dataConnection
        ? DC_TYPE_TO_INTEGRATION_ID[dataConnection.type]
        : undefined
    const nangoConnectionId = dataConnection?.nango_connection_id ?? ''

    const { hasAuthError, isSyncInProgress, lastSyncTime, earliestStatus } =
        calculateSyncState(dataConnections)

    const { data: webhooks = [] } = useGetWebhooks(nangoConnectionId, integrationId)
    const { hasWebhookError } = calculateWebhookState(webhooks)

    let status = 'finished'
    if (isSyncInProgress) {
        status = 'in-progress'
    }
    if (hasAuthError || hasWebhookError) {
        status = 'error'
    }

    let buttonLabel = 'Synced'
    if (status === 'in-progress') {
        buttonLabel = 'Syncing...'
    } else if (status === 'error') {
        buttonLabel = 'Connection error'
    } else if (lastSyncTime) {
        buttonLabel = `Synced ${moment(lastSyncTime).fromNow()}`
    }

    let buttonLabelColor = theme.color.textSuccess
    if (status === 'in-progress') {
        buttonLabelColor = theme.color.textWeakest
    } else if (status === 'error') {
        buttonLabelColor = theme.color.textError
    }

    const isVisible = !!integrationId && !!dataConnection && !isStackerNativeObject

    const toast = useToast()

    const showSyncButton = status !== 'in-progress'

    const { mutateAsync: syncConnection } = useSyncConnection({
        onError: () => {
            toast({
                type: 'error',
                title: 'There was a problem triggering a sync',
                startIcon: { name: 'AlertCircle' },
                helperText: 'Please try again later. If the issue persists, contact support.',
            })
        },
    })

    const onForceFullSync = () => {
        if (!dataConnection) return

        syncConnection({
            nangoConnectionId: dataConnection.nango_connection_id ?? '',
            dataConnectionType: dataConnection.type,
        })
    }

    return {
        earliestStatus,
        dataConnections,
        isOpen,
        onOpenChange: setIsOpen,
        integrationId,
        isVisible,
        buttonLabel,
        buttonLabelColor,
        status,
        onForceFullSync,
        showSyncButton,
    }
}
